import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Download,
  Eye,
  FileText,
  Search,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";
import jsPDF from "jspdf";

import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";

const statusStyle = {
  Acknowledged: "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-200 dark:border-emerald-900",
  Pending: "bg-[var(--accent-light)] text-[var(--heading-secondary)] border-[var(--border-color)] dark:bg-[var(--accent-subtle)] dark:text-blue-200 dark:border-[var(--border-color)]",
  Overdue: "bg-red-50 text-red-700 border-red-100 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900",
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const makeHtmlDocument = (html) => `data:text/html;charset=utf-8,${encodeURIComponent(html)}`;

const formatRoleLabel = (role = "") => {
  const value = String(role || "").trim();
  if (!value) return "";
  return value
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const employeePlatformGuide = {
  title: "Employee Platform Guide",
  category: "Platform Usage",
  version: "1.5",
  name: "employee-platform-guide.html",
  type: "text/html",
  data: makeHtmlDocument(`
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Employee Platform Guide</title>
  <style>
    body { font-family: 'Plus Jakarta Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #111827; background: #f8fafc; margin: 0; padding: 28px; line-height: 1.55; }
    main { max-width: 880px; margin: auto; background: white; border: 1px solid #e5e7eb; border-radius: 16px; padding: 28px; }
    h1 { margin: 0 0 8px; font-size: 30px; }
    h2 { margin-top: 26px; font-size: 18px; color: #4f46e5; }
    p, li { font-size: 14px; }
    .note { background: #eef2ff; border: 1px solid #c7d2fe; border-radius: 12px; padding: 12px 14px; font-weight: 700; }
    .meta { color: #64748b; font-size: 12px; margin-bottom: 18px; }
    .callout { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px 14px; }
    ul { padding-left: 20px; }
  </style>
</head>
<body>
  <main>
    <h1>Employee Platform Guide</h1>
    <p class="meta">Platform Usage | Version 1.5 | Employee portal guide</p>
    <p class="note">This guide explains how employees, leads, and admins should use the Aviatricks Attendance and HR portal for daily work, attendance, EOD reports, leave, expenses, tasks, announcements, profile updates, chat, policies, payroll records, and security.</p>

    <h2>1. What this platform is for</h2>
    <p>The platform is used to manage attendance, attendance corrections, EOD reports, leaves, expenses, tasks, team task progress, announcements, grievances, policies, salary slips, payslip issues, chat, employee availability, profile documents, profile change requests, offboarding, and approvals in one place.</p>

    <h2>2. Login and first-time access</h2>
    <ul>
      <li>Use the employee portal login page with your official email and password.</li>
      <li>If you are a new employee, complete the set-password flow from the invitation or setup link provided by the company.</li>
      <li>If you forget your password, use the reset-password flow and follow the reset email instructions.</li>
      <li>Do not use another employee's login or ask anyone to log in on your behalf.</li>
      <li>After login, review your dashboard, pending actions, notifications, and profile completion status.</li>
    </ul>

    <h2>3. Dashboard and navigation</h2>
    <ul>
      <li>The dashboard gives a quick view of attendance state, assigned tasks, announcements, EOD status, and important reminders.</li>
      <li>Use the left navigation or top navigation to move between Attendance, EOD, Leave, Expenses, Tasks, Announcements, Policies, Chat, Grievance, Offboarding, and Profile.</li>
      <li>Leads may also see team views for team attendance, team EOD, team leave, and team task progress.</li>
      <li>Use notifications to identify items that need action, such as pending policies, approvals, announcements, and updates.</li>
      <li>If a page looks outdated after a change, refresh the page once before reporting an issue.</li>
    </ul>

    <h2>4. Daily portal checklist</h2>
    <ul>
      <li>At the start of the day: log in, check notifications, review tasks, and complete attendance check-in.</li>
      <li>During the day: update task progress, use chat for work communication, and keep blockers visible to the right team member.</li>
      <li>Before leaving: end lunch break if active, check out, complete EOD, and verify the saved/submitted indication.</li>
      <li>Every week: review pending policies, profile completion, leave balance, expense status, and assigned tasks.</li>
      <li>Every month: check salary slip availability and confirm profile/bank details are still accurate.</li>
    </ul>

    <h2>5. Daily attendance</h2>
    <ul>
      <li>Open the Attendance page at the start of the workday and use Check In.</li>
      <li>Use Start Lunch Break and End Lunch Break only during your actual break.</li>
      <li>Use Check Out before the day ends. After final checkout, the EOD report page may open automatically.</li>
      <li>The Workday tracker shows the current day and date so employees can verify they are working on the correct day.</li>
      <li>If you forget check-in, checkout, or make a mistake, raise an attendance correction request with a clear reason.</li>
      <li>Lead-reviewed correction requests may be sent to admin for final approval where configured.</li>
      <li>Do not check in or check out for another employee.</li>
      <li>Use correction requests only for genuine mistakes or missed punches, not as a replacement for daily check-in discipline.</li>
    </ul>

    <h2>6. EOD reports</h2>
    <ul>
      <li>Submit your EOD report before the configured deadline.</li>
      <li>Write clear work updates, blockers, completed work, pending items, and tomorrow's plan.</li>
      <li>Use the save/submission indication to confirm that your report was saved or submitted successfully.</li>
      <li>Keep updates professional and specific. Avoid one-word summaries unless there was genuinely no work activity.</li>
      <li>If you submit a draft first, complete and submit the final report before the deadline.</li>
      <li>Mention blockers early so the concerned team member can help remove delays.</li>
    </ul>

    <h2>7. Leaves</h2>
    <ul>
      <li>Apply leave from the Leave page with correct dates, type, and reason.</li>
      <li>Your balance updates based on admin approval and company leave rules.</li>
      <li>If leave is approved for the current day, your chat/profile status can show Leave automatically.</li>
      <li>Submit leave requests in advance wherever possible.</li>
      <li>Check the leave status after submission. Approved, rejected, or pending states may appear in the Leave page and notifications.</li>
      <li>If a leave is rejected, review the reason before submitting a corrected request.</li>
    </ul>

    <h2>8. Expenses</h2>
    <ul>
      <li>Submit expense claims with date, amount, description, and receipt if required.</li>
      <li>Approved expenses may be considered in payroll based on company rules.</li>
      <li>If an expense is rejected, check the rejection reason in notifications or the Expenses page.</li>
      <li>Use clear descriptions and upload valid supporting documents when required.</li>
      <li>Do not submit duplicate expenses or personal expenses as company claims.</li>
    </ul>

    <h2>9. Request status and approvals</h2>
    <ul>
      <li>Leave, expense, and attendance correction requests show pending, approved, or rejected status in the relevant page.</li>
      <li>Employees should check the relevant page and notifications for approval, rejection, and rejection reasons.</li>
      <li>Submit accurate details and supporting documents where required because request history is stored in the platform.</li>
      <li>Do not repeatedly submit the same request unless the previous request was rejected or incorrect.</li>
    </ul>

    <h2>10. Tasks and announcements</h2>
    <ul>
      <li>Check assigned tasks regularly and update task status when work progresses.</li>
      <li>Task status should reflect the actual work state: pending, in progress, blocked, or completed.</li>
      <li>If a task is blocked, use the escalation or blocker note where available, and communicate it through chat or EOD.</li>
      <li>Employees can see who assigned a task and when it was assigned.</li>
      <li>Leads can assign tasks to selected team members or the whole team, and can review team task progress.</li>
      <li>Read announcements for company updates, deadlines, policy updates, holiday notices, operational updates, and reminders.</li>
      <li>Announcements may be pinned, marked read, viewed in full, and opened with Read more when the content is long.</li>
      <li>Leads can create announcements for all teammates or selected teammates. The announcement view shows who it was assigned to.</li>
      <li>Admins can review announcements, see which lead posted them, see assigned employees, pin or unpin posts, and delete posts if needed.</li>
    </ul>

    <h2>11. Chat and employee status</h2>
    <ul>
      <li>Use chat to message admin or employees for work-related communication.</li>
      <li>Files shared in chat can be opened inside the website.</li>
      <li>PDF, image, and spreadsheet attachments may open in an in-app preview when supported.</li>
      <li>Set your status correctly: Active, Busy, Do Not Disturb, or Leave.</li>
      <li>Use file attachments only for work-related screenshots, documents, or evidence.</li>
      <li>Do not share passwords, private personal documents, or unrelated media through chat.</li>
      <li>Use Do Not Disturb only when genuinely unavailable for immediate communication.</li>
    </ul>

    <h2>12. Profile, bank details, and document uploads</h2>
    <ul>
      <li>Keep personal details, contact details, emergency contact, regulatory IDs, bank details, and biography updated.</li>
      <li>Upload required profile documents such as offer letter, bank passbook, Aadhaar card, ID card photo, and service agreement.</li>
      <li>Profile documents may show validity or expiry reminders where applicable.</li>
      <li>When uploading a document, wait for the upload progress and uploaded state before closing the profile modal.</li>
      <li>After clicking Save Changes, wait for the Saving and Saved changes indication.</li>
      <li>Once an admin verifies and locks a profile, employees should submit a profile change request for locked details instead of editing them directly.</li>
      <li>Admins may review, approve, or reject profile change requests through the approval/admin workflow.</li>
      <li>If a document already shows as uploaded, do not upload it again unless the file needs to be replaced.</li>
      <li>Use correct IFSC, account number, PAN, Aadhaar, UAN, and ESI values where applicable.</li>
      <li>Upload clear images or PDFs. Blurry, cropped, password-protected, or unrelated files may need to be replaced.</li>
      <li>If your profile completion is below 100%, check the missing fields and complete them as soon as possible.</li>
    </ul>

    <h2>13. Salary slips and payroll-related records</h2>
    <ul>
      <li>Use the Salary Slip section to view available salary slips issued by the company.</li>
      <li>Check your bank and statutory details in your profile before payroll processing.</li>
      <li>Raise a payslip issue if salary slip details, employee details, or bank details appear incorrect.</li>
      <li>Payroll-locked periods may prevent edits to salary, leave, LOP, bank details, or attendance until the relevant salary slip is unlocked by admin.</li>
      <li>Do not share salary slips publicly or with unauthorized people.</li>
    </ul>

    <h2>14. Grievance, complaints, and offboarding</h2>
    <ul>
      <li>Use Grievance to report issues involving another employee.</li>
      <li>Use Request for general help or HR requests that do not involve another employee.</li>
      <li>Track the status after submission and provide extra information if requested.</li>
      <li>Provide facts, dates, people involved, and supporting information when raising a complaint.</li>
      <li>Use offboarding workflows only for official resignation, clearance, settlement, or final document processes.</li>
      <li>Admins may process offboarding status and upload final documents such as relieving or experience letters where applicable.</li>
    </ul>

    <h2>15. Policies</h2>
    <ul>
      <li>Open every assigned policy, read it fully, then click I have read and acknowledge.</li>
      <li>Mandatory policies may have due dates. Pending and overdue policies can appear in notifications.</li>
      <li>Your acknowledgement date and time are recorded for compliance.</li>
      <li>When a revised policy version is published, employees may be asked to review and acknowledge the new version.</li>
      <li>Do not acknowledge a policy without reading it. Acknowledgement confirms that you have reviewed and understood the document.</li>
    </ul>

    <h2>16. Lead responsibilities</h2>
    <ul>
      <li>Leads should review team attendance, EOD submissions, leave requests, task progress, and blockers regularly.</li>
      <li>When creating tasks or announcements, select the correct team members and keep the content clear and work-related.</li>
      <li>Lead approvals and comments should be accurate, professional, and based on the submitted record.</li>
      <li>Leads should use team dashboards to identify missing checkouts, missing EOD reports, pending leave, and blocked work early.</li>
    </ul>

    <h2>17. Admin and control-center workflows</h2>
    <ul>
      <li>Admins use the Control Center to review readiness, alerts, payroll locks, missing checkouts, missing EOD, high LOP, employee risks, lifecycle items, reports, templates, audit trail, and role boundaries.</li>
      <li>Admins can review approval inbox items, attendance corrections, profile change requests, payslip issues, leave, expenses, policies, offboarding, WFH, geofencing, employees, and teams.</li>
      <li>Admins can create, archive, preview, and track policies, including acknowledgement reports and pending or overdue status.</li>
      <li>Admins can inspect chat, export monitored chat ranges, review attachments, and manage employee-facing records.</li>
      <li>Admin actions may be recorded in the audit trail for payroll, attendance, leave, bank, PAN, employee, and related record changes.</li>
    </ul>

    <h2>18. Data accuracy and record responsibility</h2>
    <ul>
      <li>Attendance, leave, expense, profile, and EOD information may be used for HR, payroll, compliance, and operational review.</li>
      <li>Employees are responsible for entering accurate information and correcting mistakes as soon as they notice them.</li>
      <li>Do not intentionally submit false attendance, false expenses, incorrect profile data, or misleading EOD updates.</li>
      <li>If you are unsure which module to use, ask the concerned HR or reporting contact before submitting incorrect records.</li>
    </ul>

    <h2>19. Troubleshooting and support</h2>
    <ul>
      <li>If a save action is taking time, wait for the Saving or Saved changes indication before closing the page.</li>
      <li>If an uploaded document already shows as uploaded by name, do not upload it again unless replacement is required.</li>
      <li>If a file preview or download does not open, check your network and share the file name and page name with the support contact.</li>
      <li>If a request, EOD, or policy does not appear after submission, refresh once and then report the issue if it remains.</li>
      <li>When reporting a technical issue, include the page name, action taken, approximate time, and screenshot if possible.</li>
    </ul>

    <h2>20. Security and responsible use</h2>
    <ul>
      <li>Do not share your login credentials.</li>
      <li>Do not upload personal, unrelated, or unsafe files into company records or chat.</li>
      <li>Use accurate information in attendance, EOD, leave, expense, and profile records.</li>
      <li>Use the platform only for official company work and records.</li>
      <li>Log out from shared or public systems after use.</li>
      <li>Report suspicious access, wrong data, or technical issues immediately.</li>
    </ul>

    <div class="callout">
      <strong>Important:</strong> If anything looks incorrect after saving, refresh the page once and check again. If the issue remains, report it with the page name and a short description.
    </div>
  </main>
</body>
</html>
  `),
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isImage = (policy) => String(policy?.document?.type || "").startsWith("image/");
const isPdf = (policy) => String(policy?.document?.type || policy?.document?.data || "").includes("pdf");
const isHtml = (policy) => String(policy?.document?.type || policy?.document?.data || "").includes("html");

const StatusPill = ({ status }) => (
  <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-black ${statusStyle[status] || statusStyle.Pending}`}>
    {status || "Pending"}
  </span>
);

const PolicyPreview = ({ policy, onClose }) => {
  if (!policy) return null;
  return (
    <div className="fixed inset-0 z-[240] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="m-auto flex h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-[var(--border-color)] px-4 py-3">
          <div className="min-w-0">
            <p className="text-xs font-black uppercase text-[var(--accent-primary)]">Policy preview</p>
            <h3 className="truncate text-sm font-black">{policy.title}</h3>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-xl hover:bg-[var(--bg-secondary)]">
            <X size={18} />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-[var(--bg-primary)] p-4">
          {isImage(policy) ? (
            <img src={policy.document.data} alt={policy.document.name || policy.title} className="max-h-full max-w-full rounded-xl object-contain" />
          ) : isPdf(policy) || isHtml(policy) ? (
            <iframe title={policy.title} src={policy.document.data} className="h-full min-h-[70vh] w-full rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)]" />
          ) : (
            <div className="max-w-md rounded-2xl border border-dashed border-[var(--border-color)] bg-[var(--card-bg)] p-8 text-center">
              <FileText size={30} className="mx-auto mb-3 text-[var(--accent-primary)]" />
              <p className="text-sm font-black">{policy.document?.name || policy.title}</p>
              <p className="mt-1 text-xs text-[var(--text-secondary)]">This file type may not preview in the browser.</p>
            </div>
          )}
        </div>
        <div className="flex justify-end border-t border-[var(--border-color)] px-4 py-3">
          <button type="button" onClick={onClose} className="rounded-xl bg-[var(--bg-secondary)] px-4 py-2 text-xs font-black">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminPolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [previewPolicy, setPreviewPolicy] = useState(null);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [indicator, setIndicator] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    category: "General",
    version: employeePlatformGuide.version,
    effectiveDate: new Date().toISOString().slice(0, 10),
    dueDate: "",
    mandatory: true,
    targetMode: "all",
    targetDepartments: [],
    targetRoles: [],
    targetEmployees: [],
  });
  const [documentFile, setDocumentFile] = useState(null);
  const [predefinedDocument, setPredefinedDocument] = useState(null);

  const selectedPolicy = useMemo(
    () => policies.find((policy) => policy._id === selectedId) || policies[0] || null,
    [policies, selectedId],
  );

  const departments = useMemo(() =>
    [...new Set(employees.map((employee) => employee.team).filter(Boolean))].sort(),
  [employees]);

  const roles = useMemo(() =>
    [...new Set(employees.map((employee) => employee.role).filter(Boolean))].sort(),
  [employees]);

  const fetchPolicies = useCallback(async () => {
    try {
      const res = await api.get("/api/admin/policies", {
        cache: false,
        params: showArchived ? { archived: true } : {},
      });
      const next = Array.isArray(res.data) ? res.data : [];
      setPolicies(next);
      if (!selectedId && next[0]?._id) setSelectedId(next[0]._id);
    } catch (error) {
      console.error("Failed to load policies:", error);
    }
  }, [selectedId, showArchived]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await api.get("/api/admin/employees/all");
      setEmployees(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to load employees:", error);
    }
  }, []);

  useEffect(() => {
        document.title = "Policies | Savrion Technologies";
    fetchPolicies();
    fetchEmployees();
  }, [fetchEmployees, fetchPolicies]);

  const filteredPolicies = policies.filter((policy) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return [policy.title, policy.category, policy.version]
      .some((value) => String(value || "").toLowerCase().includes(query));
  });

  const setMulti = (field, value, checked) => {
    setForm((current) => {
      const set = new Set(current[field] || []);
      if (checked) set.add(value);
      else set.delete(value);
      return { ...current, [field]: [...set] };
    });
  };

  const applyEmployeePlatformGuide = () => {
    setForm((current) => ({
      ...current,
      title: employeePlatformGuide.title,
      category: employeePlatformGuide.category,
      version: employeePlatformGuide.version,
      mandatory: true,
      targetMode: "all",
      targetDepartments: [],
      targetRoles: [],
      targetEmployees: [],
    }));
    setDocumentFile(null);
    setPredefinedDocument(employeePlatformGuide);
    setIndicator("Predefined employee platform guide selected.");
    window.setTimeout(() => setIndicator(""), 1800);
  };

  const createPolicy = async (event) => {
    event.preventDefault();
    if ((!documentFile && !predefinedDocument) || !form.title.trim() || !form.effectiveDate) {
      setIndicator("Policy title, date and document are required.");
      window.setTimeout(() => setIndicator(""), 2200);
      return;
    }

    setSaving(true);
    setIndicator("Publishing policy...");
    try {
      const documentData = predefinedDocument?.data || await fileToDataUrl(documentFile);
      const res = await api.post("/api/admin/policies", {
        ...form,
        documentName: predefinedDocument?.name || documentFile.name,
        documentType: predefinedDocument?.type || documentFile.type,
        documentData,
      }, { skipSaveToast: true });

      setPolicies((current) => [res.data, ...current]);
      setSelectedId(res.data._id);
      setDocumentFile(null);
      setPredefinedDocument(null);
      setForm((current) => ({ ...current, title: "", category: "General", version: employeePlatformGuide.version, dueDate: "", targetEmployees: [] }));
      setIndicator("Policy published.");
    } catch (error) {
      setIndicator(error.response?.data?.message || "Could not publish policy.");
    } finally {
      setSaving(false);
      window.setTimeout(() => setIndicator(""), 2400);
    }
  };

  const exportPdf = (policy) => {
    if (!policy) return;
    const doc = new jsPDF();
    const rows = policy.report || [];
    let y = 18;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Policy Acknowledgement Report", 14, y);
    y += 8;
    doc.setFontSize(10);
    doc.text(`${policy.title} | ${policy.category} | v${policy.version}`, 14, y);
    y += 8;
    doc.setFont("helvetica", "normal");
    doc.text(`Target: ${policy.targetCount}   Acknowledged: ${policy.acknowledgedCount}   Pending: ${policy.pendingCount}   Overdue: ${policy.overdueCount}`, 14, y);
    y += 10;
    doc.setFont("helvetica", "bold");
    doc.text("Employee", 14, y);
    doc.text("ID", 74, y);
    doc.text("Status", 106, y);
    doc.text("Acknowledged", 146, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    rows.forEach((item, index) => {
      if (y > 280) {
        doc.addPage();
        y = 18;
      }
      doc.text(String(index + 1), 8, y);
      doc.text(String(item.employee?.name || "-").slice(0, 26), 14, y);
      doc.text(String(item.employee?.employeeId || "-").slice(0, 14), 74, y);
      doc.text(String(item.status || "-"), 106, y);
      doc.text(formatDateTime(item.acknowledgedAt).slice(0, 24), 146, y);
      y += 7;
    });
    doc.save(`policy_acknowledgement_${policy.title.replace(/[^a-z0-9]+/gi, "_")}.pdf`);
  };

  const archivePolicy = async (policy) => {
    if (!policy || !window.confirm(`Archive ${policy.title}?`)) return;
    const previous = policies;
    setPolicies((current) => current.filter((item) => item._id !== policy._id));
    if (selectedId === policy._id) setSelectedId("");
    try {
      await api.delete(`/api/admin/policies/${policy._id}`, { skipSaveToast: true });
      setIndicator("Policy archived.");
    } catch (error) {
      setPolicies(previous);
      setIndicator(error.response?.data?.message || "Could not archive policy.");
    } finally {
      window.setTimeout(() => setIndicator(""), 2200);
    }
  };

  const hardDeletePolicy = async (policy) => {
    if (!policy || !window.confirm(`Are you sure you want to permanently delete ${policy.title}? This cannot be undone.`)) return;
    const previous = policies;
    setPolicies((current) => current.filter((item) => item._id !== policy._id));
    if (selectedId === policy._id) setSelectedId("");
    try {
      await api.delete(`/api/admin/policies/${policy._id}/hard`, { skipSaveToast: true });
      setIndicator("Policy deleted permanently.");
    } catch (error) {
      setPolicies(previous);
      setIndicator(error.response?.data?.message || "Could not delete policy.");
    } finally {
      window.setTimeout(() => setIndicator(""), 2200);
    }
  };

  const unarchivePolicy = async (policy) => {
    if (!policy || !window.confirm(`Unarchive ${policy.title}?`)) return;
    const previous = policies;
    setPolicies((current) => current.filter((item) => item._id !== policy._id));
    try {
      await api.patch(`/api/admin/policies/${policy._id}`, { active: true }, { skipSaveToast: true });
      setIndicator("Policy unarchived.");
    } catch (error) {
      setPolicies(previous);
      setIndicator(error.response?.data?.message || "Could not unarchive policy.");
    } finally {
      window.setTimeout(() => setIndicator(""), 2200);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg-primary)] text-[var(--text-primary)] xl:flex-row">
      <AdminNavbar />
      <main className="min-w-0 flex-1 px-4 py-6 md:px-6">
        <div className="space-y-5">
          <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-wide text-[var(--accent-primary)] dark:text-[var(--heading-secondary)]">
                  <ShieldCheck size={16} />
                  Compliance
                </div>
                <h1 className="text-2xl font-black md:text-3xl">Policy acknowledgement</h1>
                <p className="mt-1 text-sm text-[var(--text-secondary)]">
                  Publish company policies and track who opened and acknowledged them.
                </p>
              </div>
              {indicator && (
                <div className="rounded-xl border border-[var(--border-color)] bg-[var(--accent-light)] px-4 py-3 text-sm font-black text-[var(--heading-secondary)] dark:border-[var(--border-color)] dark:bg-[var(--accent-subtle)] dark:text-blue-200">
                  {indicator}
                </div>
              )}
            </div>
          </section>

          <div className="grid gap-5 2xl:grid-cols-[430px_1fr]">
            <form onSubmit={createPolicy} className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5">
              <h2 className="text-lg font-black">Upload policy</h2>
              <div className="mt-4 space-y-4">
                <button
                  type="button"
                  onClick={applyEmployeePlatformGuide}
                  className="flex w-full items-center justify-between rounded-xl border border-[var(--border-color)] bg-[var(--accent-light)] px-3 py-3 text-left text-sm font-black text-[var(--accent-primary)] dark:bg-[var(--accent-subtle)] dark:text-[var(--accent-primary)]"
                >
                  <span>Use predefined Employee Platform Guide v{employeePlatformGuide.version}</span>
                  <ShieldCheck size={16} />
                </button>
                <input value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} placeholder="Policy title" className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-bold outline-none" />
                <div className="grid gap-3 sm:grid-cols-2">
                  <input value={form.category} onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))} placeholder="Category" className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-bold outline-none" />
                  <input value={form.version} onChange={(event) => setForm((current) => ({ ...current, version: event.target.value }))} placeholder="Version" className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-bold outline-none" />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs font-black uppercase text-[var(--text-secondary)]">Effective</span>
                    <input type="date" value={form.effectiveDate} onChange={(event) => setForm((current) => ({ ...current, effectiveDate: event.target.value }))} className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-bold outline-none" />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-black uppercase text-[var(--text-secondary)]">Due date</span>
                    <input type="date" value={form.dueDate} onChange={(event) => setForm((current) => ({ ...current, dueDate: event.target.value }))} className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-bold outline-none" />
                  </label>
                </div>

                <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-dashed border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-3 text-sm font-bold">
                  <span className="flex min-w-0 items-center gap-2">
                    <Upload size={16} className="text-[var(--accent-primary)]" />
                    <span className="truncate">{predefinedDocument?.name || documentFile?.name || "Upload PDF, image or Word file"}</span>
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                    onChange={(event) => {
                      setDocumentFile(event.target.files?.[0] || null);
                      setPredefinedDocument(null);
                    }}
                  />
                </label>

                <label className="flex items-center gap-2 text-sm font-bold">
                  <input type="checkbox" checked={form.mandatory} onChange={(event) => setForm((current) => ({ ...current, mandatory: event.target.checked }))} className="h-4 w-4 accent-blue-600" />
                  Mandatory acknowledgement
                </label>

                <select value={form.targetMode} onChange={(event) => setForm((current) => ({ ...current, targetMode: event.target.value }))} className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-bold outline-none">
                  <option value="all">All employees</option>
                  <option value="department">Department wise</option>
                  <option value="role">Role wise</option>
                  <option value="selected">Selected employees</option>
                </select>

                {form.targetMode === "department" && (
                  <div className="max-h-40 overflow-auto rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-3">
                    {departments.map((department) => (
                      <label key={department} className="mb-2 flex items-center gap-2 text-sm font-bold">
                        <input type="checkbox" checked={form.targetDepartments.includes(department)} onChange={(event) => setMulti("targetDepartments", department, event.target.checked)} />
                        {department}
                      </label>
                    ))}
                  </div>
                )}

                {form.targetMode === "role" && (
                  <div className="max-h-40 overflow-auto rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-3">
                    {roles.map((role) => (
                      <label key={role} className="mb-2 flex items-center gap-2 text-sm font-bold">
                        <input type="checkbox" checked={form.targetRoles.includes(role)} onChange={(event) => setMulti("targetRoles", role, event.target.checked)} />
                        {formatRoleLabel(role)}
                      </label>
                    ))}
                  </div>
                )}

                {form.targetMode === "selected" && (
                  <div className="max-h-52 overflow-auto rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-3">
                    {employees.map((employee) => (
                      <label key={employee._id} className="mb-2 flex items-center gap-2 text-sm font-bold">
                        <input type="checkbox" checked={form.targetEmployees.includes(employee._id)} onChange={(event) => setMulti("targetEmployees", employee._id, event.target.checked)} />
                        <span className="min-w-0 truncate">{employee.name} - {employee.employeeId}</span>
                      </label>
                    ))}
                  </div>
                )}

                <button type="submit" disabled={saving} className="h-11 w-full rounded-xl bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] transition-all text-sm font-black text-white disabled:opacity-60">
                  {saving ? "Publishing..." : "Publish policy"}
                </button>
              </div>
            </form>

            <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)]">
              <div className="flex flex-col gap-3 border-b border-[var(--border-color)] p-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex min-w-0 items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 lg:w-[360px]">
                  <Search size={17} className="text-[var(--text-secondary)]" />
                  <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search policies" className="min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedId("");
                      setShowArchived((current) => !current);
                    }}
                    className={`rounded-xl border px-3 py-2 text-xs font-black ${showArchived ? "border-red-200 bg-red-50 text-red-600" : "border-[var(--border-color)]"}`}
                  >
                    {showArchived ? "Archived" : "Active"}
                  </button>
                </div>
              </div>

              <div className="grid min-h-[600px] lg:grid-cols-[320px_1fr]">
                <div className="border-r border-[var(--border-color)] p-2">
                  {filteredPolicies.map((policy) => (
                    <button key={policy._id} type="button" onClick={() => setSelectedId(policy._id)} className={`mb-2 w-full rounded-xl border p-3 text-left ${selectedPolicy?._id === policy._id ? "border-[var(--accent-primary)] bg-[var(--accent-light)] dark:bg-[var(--accent-subtle)]" : "border-[var(--border-color)] bg-[var(--bg-primary)]"}`}>
                      <p className="truncate text-sm font-black">{policy.title}</p>
                      <p className="mt-1 truncate text-xs font-bold text-[var(--text-secondary)]">{policy.category} - v{policy.version}</p>
                      <div className="mt-3 flex gap-2 text-[10px] font-black">
                        <span className="text-emerald-600">{policy.acknowledgedCount} ack</span>
                        <span className="text-[var(--accent-primary)]">{policy.pendingCount} pending</span>
                        <span className="text-red-600">{policy.overdueCount} overdue</span>
                      </div>
                    </button>
                  ))}
                </div>

                {!selectedPolicy ? (
                  <div className="p-10 text-center text-sm font-bold text-[var(--text-secondary)]">No policy selected.</div>
                ) : (
                  <div className="min-w-0 p-5">
                    <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                      <div>
                        <h2 className="text-2xl font-black">{selectedPolicy.title}</h2>
                        <p className="mt-1 text-sm font-bold text-[var(--text-secondary)]">
                          {selectedPolicy.category} - v{selectedPolicy.version} - Effective {formatDate(selectedPolicy.effectiveDate)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => setPreviewPolicy(selectedPolicy)} className="inline-flex items-center gap-2 rounded-xl border border-[var(--border-color)] px-3 py-2 text-xs font-black">
                          <Eye size={14} />
                          Preview
                        </button>
                        <button type="button" onClick={() => exportPdf(selectedPolicy)} className="inline-flex items-center gap-2 rounded-xl border border-[var(--border-color)] px-3 py-2 text-xs font-black">
                          <Download size={14} />
                          PDF
                        </button>
                        {showArchived ? (
                          <button type="button" onClick={() => unarchivePolicy(selectedPolicy)} className="rounded-xl bg-emerald-500 px-3 py-2 text-xs font-black text-white">
                            Unarchive
                          </button>
                        ) : (
                          <button type="button" onClick={() => archivePolicy(selectedPolicy)} className="rounded-xl bg-amber-500 px-3 py-2 text-xs font-black text-white">
                            Archive
                          </button>
                        )}
                        <button type="button" onClick={() => hardDeletePolicy(selectedPolicy)} className="rounded-xl bg-red-500 px-3 py-2 text-xs font-black text-white">
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="mt-5 grid gap-3 md:grid-cols-4">
                      {[
                        ["Target", selectedPolicy.targetCount],
                        ["Acknowledged", selectedPolicy.acknowledgedCount],
                        ["Pending", selectedPolicy.pendingCount],
                        ["Overdue", selectedPolicy.overdueCount],
                      ].map(([label, value]) => (
                        <div key={label} className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-3">
                          <p className="text-xs font-black uppercase text-[var(--text-secondary)]">{label}</p>
                          <p className="mt-1 text-2xl font-black">{value || 0}</p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-5 overflow-hidden rounded-2xl border border-[var(--border-color)]">
                      <div className="grid grid-cols-[1.2fr_.8fr_.8fr_.8fr] bg-[var(--bg-secondary)] px-4 py-3 text-xs font-black uppercase text-[var(--text-secondary)]">
                        <span>Employee</span>
                        <span>Status</span>
                        <span>Opened</span>
                        <span>Acknowledged</span>
                      </div>
                      <div className="max-h-[420px] overflow-auto divide-y divide-[var(--border-color)]">
                        {(selectedPolicy.report || []).map((item) => (
                          <div key={item.employee?._id} className="grid grid-cols-[1.2fr_.8fr_.8fr_.8fr] gap-2 px-4 py-3 text-sm">
                            <div className="min-w-0">
                              <p className="truncate font-black">{item.employee?.name || "Employee"}</p>
                              <p className="truncate text-xs font-bold text-[var(--text-secondary)]">{item.employee?.employeeId || "-"} - {item.employee?.team || "-"}</p>
                            </div>
                            <StatusPill status={item.status} />
                            <span className="text-xs font-bold text-[var(--text-secondary)]">{formatDateTime(item.openedAt)}</span>
                            <span className="text-xs font-bold text-[var(--text-secondary)]">{formatDateTime(item.acknowledgedAt)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>
      <PolicyPreview policy={previewPolicy} onClose={() => setPreviewPolicy(null)} />
    </div>
  );
};

export default AdminPolicies;


